import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllCourses } from '../../../api/apiServices';

export default function CategoriesSection() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchAllCourses();

        if (response.success && Array.isArray(response.courses)) {
          // Extract unique categories and count courses in each
          const categoryMap = new Map();

          response.courses.forEach(course => {
            if (course.category && course.isPublished) {
              const cat = course.category;
              if (categoryMap.has(cat)) {
                categoryMap.set(cat, categoryMap.get(cat) + 1);
              } else {
                categoryMap.set(cat, 1);
              }
            }
          });

          // Convert to array with nice formatting
          const formattedCategories = Array.from(categoryMap.entries()).map(([id, count]) => ({
            id: id,
            name: id.charAt(0).toUpperCase() + id.slice(1).replace('-', ' '), // e.g., "web-dev" → "Web Dev"
            courseCount: count,
            // You can map specific colors and icons here
            color: getCategoryColor(id),
            icon: getCategoryIcon(id)
          }));

          setCategories(formattedCategories);
        } else {
          setError("Failed to load categories");
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setError("Something went wrong while loading categories");
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Helper functions for colors and icons
  const getCategoryColor = (categoryId) => {
    const colors = {
      'web-dev': 'bg-blue-100 text-blue-600',
      'mobile': 'bg-purple-100 text-purple-600',
      'data-science': 'bg-emerald-100 text-emerald-600',
      'design': 'bg-pink-100 text-pink-600',
      'marketing': 'bg-amber-100 text-amber-600',
      'business': 'bg-indigo-100 text-indigo-600',
    };
    return colors[categoryId] || 'bg-slate-100 text-slate-600';
  };

  const getCategoryIcon = (categoryId) => {
    const icons = {
      'web-dev': 'ri-code-line',
      'mobile': 'ri-smartphone-line',
      'data-science': 'ri-bar-chart-line',
      'design': 'ri-palette-line',
      'marketing': 'ri-megaphone-line',
      'business': 'ri-briefcase-line',
    };
    return icons[categoryId] || 'ri-book-line';
  };

  if (loading) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-500">Loading categories...</p>
        </div>
      </section>
    );
  }

  if (error || categories.length === 0) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-500">{error || "No categories available"}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-teal-600 text-sm font-semibold uppercase tracking-widest mb-2">Explore by Topic</p>
          <h2 className="text-4xl font-bold text-slate-900">Browse Categories</h2>
          <p className="text-slate-500 mt-2 text-base">Find courses tailored to your goals and interests</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/courses?category=${cat.id}`}
              className="group bg-white rounded-2xl p-6 border border-slate-100 hover:border-teal-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col items-center text-center"
            >
              <div className={`w-16 h-16 flex items-center justify-center rounded-2xl ${cat.color} mb-5 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${cat.icon} text-3xl`} />
              </div>

              <h3 className="font-semibold text-slate-800 text-lg mb-1 group-hover:text-teal-600 transition-colors">
                {cat.name}
              </h3>
              
              <p className="text-slate-500 text-sm">
                {cat.courseCount} courses
              </p>
            </Link>
          ))}
        </div>

        {/* Optional: View All Button */}
        <div className="text-center mt-12">
          <Link 
            to="/courses" 
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
          >
            View all categories and courses 
            <span className="text-xl">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}